import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiDollarSign, FiUsers, FiCheckCircle, FiAlertCircle,
    FiRefreshCw, FiPlay, FiLock, FiCalendar, FiClock,
} from "react-icons/fi";
import salaryService from "../../services/salaryService";
import ConfirmModal from "../../components/common/ConfirmModal";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;

const MONTHS = [
    "", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

const now = new Date();
const THIS_YEAR = now.getFullYear();
const THIS_MON = now.getMonth() + 1;
const YEAR_OPTS = Array.from({ length: 5 }, (_, i) => THIS_YEAR - i);

const EMPTY_SUMMARY = {
    total: 0,
    finalized: 0,
    totalNetPay: 0,
    totalGross: 0,
    totalLOP: 0,
};

const normalizePayrollMonth = (payload) => ({
    records: Array.isArray(payload?.data) ? payload.data : [],
    summary: payload?.summary && !Array.isArray(payload.summary) ? payload.summary : null,
});

const normalizePayrollHistory = (payload) => (
    Array.isArray(payload?.data) ? payload.data : []
);

// ─── StatCard ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, color }) {
    return (
        <div className="card" style={{ padding: "1.1rem 1.25rem", borderLeft: `4px solid ${color}` }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
                <Icon size={14} color={color} />
            </div>
            <div style={{ fontSize: 21, fontWeight: 700, color }}>{value}</div>
            <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 3 }}>{label}</div>
        </div>
    );
}

export default function PayrollDashboard() {
    const navigate = useNavigate();

    const [month, setMonth] = useState(THIS_MON);
    const [year, setYear] = useState(THIS_YEAR);
    const [workingDays, setWorkingDays] = useState(26);
    const [records, setRecords] = useState([]);
    const [summary, setSummary] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [finalizing, setFinalizing] = useState(false);
    const [confirmFinalize, setConfirmFinalize] = useState(false);

    // ── Fetch payroll for selected month ──────────────────────────────────────
    const fetchPayroll = useCallback(async () => {
        setLoading(true);
        try {
            const res = await salaryService.getByMonth(month, year);
            const { records: monthRecords, summary: monthSummary } = normalizePayrollMonth(res);
            setRecords(monthRecords);
            setSummary(monthSummary);
            if (monthRecords.length > 0) {
                setWorkingDays(Number(monthRecords[0].workingDays) || 0);
            }
        } catch (err) {
            setRecords([]);
            setSummary(null);
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, [month, year]);

    // ── Fetch all payroll months (history sidebar) ────────────────────────────
    const fetchHistory = async () => {
        try {
            const res = await salaryService.getMonths();
            setHistory(normalizePayrollHistory(res));
        } catch { /* silent */ }
    };

    useEffect(() => { fetchPayroll(); }, [fetchPayroll]);
    useEffect(() => { fetchHistory(); }, []);

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            const res = await salaryService.refreshMonth(month, year, workingDays);
            toast.success(res.message);
            await fetchPayroll();
            await fetchHistory();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setRefreshing(false);
        }
    };

    // ── Generate payroll ──────────────────────────────────────────────────────
    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const res = await salaryService.generate(month, year, workingDays);
            toast.success(res.message);
            fetchPayroll();
            fetchHistory();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setGenerating(false);
        }
    };

    // ── Finalize payroll ──────────────────────────────────────────────────────
    const handleFinalize = async () => {
        setFinalizing(true);
        try {
            const res = await salaryService.finalize(month, year);
            toast.success(res.message);
            fetchPayroll();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setFinalizing(false);
            setConfirmFinalize(false);
        }
    };

    const allFinalized = records.length > 0 && records.every((r) => r.status === "Finalized");
    const hasDrafts = records.some((r) => r.status === "Draft");

    return (
        <div>
            {/* ── Header ── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
                <div>
                    <h1 className="page-title">Payroll</h1>
                    <p className="page-subtitle">Employee salary management</p>
                </div>

                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "flex-end" }}>
                    {/* Month selector */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <label style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)", fontWeight: 700 }}>Month</label>
                        <select className="form-control" style={{ width: 140, fontSize: 13 }} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                            {MONTHS.slice(1).map((m, i) => (
                                <option key={i + 1} value={i + 1}>{m}</option>
                            ))}
                        </select>
                    </div>

                    {/* Year selector */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <label style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)", fontWeight: 700 }}>Year</label>
                        <select className="form-control" style={{ width: 110, fontSize: 13 }} value={year} onChange={(e) => setYear(Number(e.target.value))}>
                            {YEAR_OPTS.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>

                    {/* Working days */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <label style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)", fontWeight: 700 }}>Working Days</label>
                        <input
                            type="number" min="1" max="31"
                            className="form-control"
                            style={{ width: 100, fontSize: 13 }}
                            value={workingDays}
                            onChange={(e) => setWorkingDays(Number(e.target.value))}
                        />
                    </div>

                    <button className="btn btn-outline" onClick={handleRefresh} disabled={loading || refreshing} style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                        <FiRefreshCw size={13} /> {refreshing ? "Refreshing..." : "Refresh"}
                    </button>

                    {records.length === 0 && (
                        <button className="btn btn-primary" onClick={handleGenerate} disabled={generating} style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                            <FiPlay size={13} /> {generating ? "Generating..." : `Generate ${MONTHS[month]} ${year}`}
                        </button>
                    )}

                    {hasDrafts && (
                        <button className="btn btn-success" onClick={() => setConfirmFinalize(true)} style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                            <FiLock size={13} /> Finalize Payroll
                        </button>
                    )}
                </div>
            </div>

            {/* ── Summary Cards ── */}
            {summary && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "1rem", marginBottom: "1.25rem" }}>
                    <StatCard label="Employees" value={summary.total ?? EMPTY_SUMMARY.total} icon={FiUsers} color="#003b67" />
                    <StatCard label="Gross Payable" value={inr(summary.totalGross ?? EMPTY_SUMMARY.totalGross)} icon={FiDollarSign} color="#d97706" />
                    <StatCard label="Net Payable" value={inr(summary.totalNetPay ?? EMPTY_SUMMARY.totalNetPay)} icon={FiCheckCircle} color="#16a34a" />
                    <StatCard label="LOP Deductions" value={inr(summary.totalLOP ?? EMPTY_SUMMARY.totalLOP)} icon={FiAlertCircle} color="#dc2626" />
                    <StatCard label="Finalized" value={`${summary.finalized ?? EMPTY_SUMMARY.finalized}/${summary.total ?? EMPTY_SUMMARY.total}`} icon={FiLock} color="#7c3aed" />
                </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: "1rem", alignItems: "start" }}>

                {/* ── Payroll Table ── */}
                <div className="card">
                    <div className="card-header" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        <FiCalendar size={14} />
                        {MONTHS[month]} {year} — Payroll Sheet
                        {allFinalized && (
                            <span style={{ marginLeft: 6, fontSize: 11, background: "#dcfce7", color: "#15803d", padding: "2px 8px", borderRadius: 99, fontWeight: 600 }}>
                                ✓ Finalized
                            </span>
                        )}
                    </div>

                    {loading ? (
                        <div className="loading-center"><div className="spinner" />Loading payroll...</div>
                    ) : records.length === 0 ? (
                        <div className="card-body" style={{ textAlign: "center", padding: "2.5rem", color: "var(--text-muted)" }}>
                            <FiDollarSign size={40} style={{ opacity: 0.2, display: "block", margin: "0 auto 0.75rem" }} />
                            <p style={{ fontSize: 14 }}>No payroll generated for {MONTHS[month]} {year}.</p>
                            <p style={{ fontSize: 13, marginTop: 4 }}>Set working days and click <strong>Generate</strong>.</p>
                        </div>
                    ) : (
                        <div style={{ overflowX: "auto" }}>
                            <table>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Employee</th>
                                        <th style={{ textAlign: "center" }}>Days</th>
                                        <th style={{ textAlign: "right" }}>Basic</th>
                                        <th style={{ textAlign: "right" }}>Gross</th>
                                        <th style={{ textAlign: "right" }}>Deductions</th>
                                        <th style={{ textAlign: "right" }}>Net Pay</th>
                                        <th style={{ textAlign: "center" }}>Status</th>
                                        <th style={{ textAlign: "right" }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {records.map((r, i) => {
                                        const isFinalized = r.status === "Finalized";
                                        return (
                                            <tr key={r._id}>
                                                <td style={{ fontSize: 12, color: "var(--text-muted)" }}>{i + 1}</td>
                                                <td>
                                                    <div style={{ fontWeight: 600, fontSize: 13 }}>{r.employee?.fullName}</div>
                                                    <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                                        {r.employee?.employeeCode} · {r.employee?.designation}
                                                    </div>
                                                </td>
                                                <td style={{ textAlign: "center", fontSize: 13 }}>
                                                    <div>{r.presentDays}P / {r.lopDays}L</div>
                                                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>of {r.workingDays}</div>
                                                </td>
                                                <td style={{ textAlign: "right", fontSize: 13 }}>{inr(r.basicPay)}</td>
                                                <td style={{ textAlign: "right", fontSize: 13, fontWeight: 600 }}>{inr(r.grossPay)}</td>
                                                <td style={{ textAlign: "right", fontSize: 13, color: "#dc2626" }}>
                                                    {inr(r.totalDeductions)}
                                                    {r.lopDays > 0 && (
                                                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>LOP: {inr(r.lopDeduction)}</div>
                                                    )}
                                                </td>
                                                <td style={{ textAlign: "right", fontWeight: 700, color: "#16a34a" }}>{inr(r.netPay)}</td>
                                                <td style={{ textAlign: "center" }}>
                                                    <span style={{
                                                        background: isFinalized ? "#dcfce7" : "#fef9c3",
                                                        color: isFinalized ? "#15803d" : "#92400e",
                                                        padding: "2px 8px", borderRadius: 99, fontSize: 11.5, fontWeight: 600,
                                                    }}>
                                                        {r.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div style={{ display: "flex", gap: "0.35rem", justifyContent: "flex-end" }}>
                                                        <button className="btn btn-outline btn-sm" onClick={() => navigate(`/payroll/${r._id}`)}>
                                                            View
                                                        </button>
                                                        {!isFinalized && (
                                                            <button className="btn btn-outline btn-sm" onClick={() => navigate(`/payroll/${r._id}/edit`)}>
                                                                Edit
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* ── History Sidebar ── */}
                <div className="card">
                    <div className="card-header" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        <FiClock size={13} /> Payroll History
                    </div>
                    <div>
                        {history.length === 0 ? (
                            <div style={{ padding: "1rem", fontSize: 13, color: "var(--text-muted)" }}>No payroll history yet.</div>
                        ) : (
                            history.map((h) => {
                                const isSelected = h._id.month === month && h._id.year === year;
                                return (
                                    <div
                                        key={`${h._id.year}-${h._id.month}`}
                                        onClick={() => { setMonth(h._id.month); setYear(h._id.year); }}
                                        style={{
                                            padding: "0.65rem 1rem",
                                            borderBottom: "1px solid var(--border)",
                                            cursor: "pointer",
                                            background: isSelected ? "var(--primary-light)" : "transparent",
                                            transition: "background 0.1s",
                                        }}
                                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "#f8fafc"; }}
                                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                                    >
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <span style={{ fontWeight: 600, fontSize: 13, color: isSelected ? "var(--primary)" : "var(--text)" }}>
                                                {MONTHS[h._id.month]} {h._id.year}
                                            </span>
                                            {h.finalized === h.count && (
                                                <FiCheckCircle size={12} color="#16a34a" />
                                            )}
                                        </div>
                                        <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2 }}>
                                            {h.count} employees · {inr(h.totalNetPay)}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>

            {/* ── Finalize Confirm ── */}
            {confirmFinalize && (
                <ConfirmModal
                    title="Finalize Payroll"
                    message={`Finalize all Draft records for ${MONTHS[month]} ${year}? Finalized records cannot be edited.`}
                    onConfirm={handleFinalize}
                    onCancel={() => setConfirmFinalize(false)}
                    loading={finalizing}
                />
            )}
        </div>
    );
}
